import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import type { TelemetrySession } from "../lib/types";

interface Props {
  session: TelemetrySession;
}

/**
 * The way out for one session, whose driving would otherwise be open to your teams
 * and its best laps to the public board. Marking it private takes it off every
 * leaderboard and out of everyone else's reach, and changes nothing for you.
 */
export function SessionPrivacyToggle({ session }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const change = useMutation({
    mutationFn: (isPrivate: boolean) => api.setSessionPrivate(session.id, isPrivate),
    onSuccess: (updated) => {
      queryClient.setQueryData(["session", session.id], updated);
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const isPrivate = session.is_private;

  return (
    <button
      type="button"
      onClick={() => change.mutate(!isPrivate)}
      disabled={change.isPending}
      title={t(
        isPrivate ? "session.privacy.privateTitle" : "session.privacy.sharedTitle",
      )}
      className={[
        "rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-50",
        isPrivate
          ? "border-amber-800/70 text-amber-400 hover:border-amber-700"
          : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200",
      ].join(" ")}
    >
      {t(isPrivate ? "session.privacy.private" : "session.privacy.shared")}
    </button>
  );
}
